import { supabase } from "../lib/supabase";

const STORAGE_KEY = "almoune_loyalty_points";
const TRANSACTIONS_KEY = "almoune_loyalty_transactions";

const readCache = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); }
  catch { return []; }
};

const writeCache = (key, data) => {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch {}
};

const normalize = (row) => ({
  id: row.id || "",
  customerId: row.customer_id || row.customerId || "",
  customerName: row.customer_name || row.customerName || "",
  customerPhone: row.customer_phone || row.customerPhone || "",
  balance: Number(row.balance || 0),
  totalEarned: Number(row.total_earned || row.totalEarned || 0),
  totalRedeemed: Number(row.total_redeemed || row.totalRedeemed || 0),
  createdAt: row.created_at || row.createdAt || new Date().toISOString(),
  updatedAt: row.updated_at || row.updatedAt || new Date().toISOString(),
});

const normalizeTransaction = (row) => ({
  id: row.id || "",
  customerId: row.customer_id || row.customerId || "",
  type: row.type || "earn",
  points: Number(row.points || 0),
  orderId: row.order_id || row.orderId || "",
  orderAmount: Number(row.order_amount || row.orderAmount || 0),
  note: row.note || "",
  createdAt: row.created_at || row.createdAt || new Date().toISOString(),
});

export const loadLoyaltyPoints = async () => {
  const cache = readCache(STORAGE_KEY);
  try {
    if (!supabase) return cache;
    const { data, error } = await supabase.from("loyalty_points").select("*").order("created_at", { ascending: false });
    if (error) throw error;
    const rows = (data || []).map(normalize);
    writeCache(STORAGE_KEY, rows);
    return rows;
  } catch { return cache; }
};

export const saveLoyaltyPoint = async (record) => {
  const normalized = {
    id: record.id,
    customer_id: record.customerId || record.customer_id,
    customer_name: record.customerName || record.customer_name,
    customer_phone: record.customerPhone || record.customer_phone,
    balance: record.balance,
    total_earned: record.totalEarned || record.total_earned || 0,
    total_redeemed: record.totalRedeemed || record.total_redeemed || 0,
  };
  const cache = readCache(STORAGE_KEY);
  const idx = cache.findIndex((r) => r.id === normalized.id);
  if (idx >= 0) cache[idx] = { ...cache[idx], ...normalized };
  else cache.push(normalized);
  writeCache(STORAGE_KEY, cache);
  try {
    if (supabase) {
      const { error } = await supabase.from("loyalty_points").upsert(normalized, { onConflict: "id" });
      if (error) throw error;
    }
  } catch {}
  return normalize(normalized);
};

export const findLoyaltyByPhone = async (phone) => {
  if (!phone) return null;
  const normalized = phone.replace(/\s+/g, "").replace(/^(\+212|00212|0)/, "0");
  const cache = readCache(STORAGE_KEY);
  const match = cache.find((r) => {
    const p = (r.customerPhone || r.customer_phone || "").replace(/\s+/g, "").replace(/^(\+212|00212|0)/, "0");
    return p === normalized;
  });
  if (match) return normalize(match);
  return null;
};

export const earnPoints = async (customerId, customerName, customerPhone, orderId, orderAmount) => {
  const pts = Math.floor(Number(orderAmount) / 20);
  if (pts <= 0) return null;
  let cache = readCache(STORAGE_KEY);
  let record = cache.find((r) => r.customerId === customerId);
  if (!record) {
    record = {
      id: `loyalty-${customerId}`,
      customerId,
      customerName: customerName || "",
      customerPhone: customerPhone || "",
      balance: 0,
      totalEarned: 0,
      totalRedeemed: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }
  record.balance += pts;
  record.totalEarned += pts;
  record.updatedAt = new Date().toISOString();
  await saveLoyaltyPoint(record);
  const txn = {
    id: `ltxn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    customerId,
    type: "earn",
    points: pts,
    orderId: orderId || "",
    orderAmount: Number(orderAmount),
    note: `Achat ${Number(orderAmount).toFixed(2)} EUR`,
    createdAt: new Date().toISOString(),
  };
  await saveLoyaltyTransaction(txn);
  return { record: normalize(record), pointsEarned: pts };
};

export const redeemPoints = async (customerId, orderId, points) => {
  const pts = Number(points);
  if (pts <= 0) return null;
  let cache = readCache(STORAGE_KEY);
  let record = cache.find((r) => r.customerId === customerId);
  if (!record || record.balance < pts) return null;
  record.balance -= pts;
  record.totalRedeemed += pts;
  record.updatedAt = new Date().toISOString();
  await saveLoyaltyPoint(record);
  const txn = {
    id: `ltxn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    customerId,
    type: "redeem",
    points: pts,
    orderId: orderId || "",
    orderAmount: 0,
    note: `Utilisation ${pts} points`,
    createdAt: new Date().toISOString(),
  };
  await saveLoyaltyTransaction(txn);
  return normalize(record);
};

export const adjustPoints = async (customerId, points, note) => {
  const pts = Number(points);
  if (pts === 0) return null;
  let cache = readCache(STORAGE_KEY);
  let record = cache.find((r) => r.customerId === customerId);
  if (!record) return null;
  record.balance += pts;
  if (pts > 0) record.totalEarned += pts;
  else record.totalRedeemed += Math.abs(pts);
  record.updatedAt = new Date().toISOString();
  await saveLoyaltyPoint(record);
  const txn = {
    id: `ltxn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    customerId,
    type: pts > 0 ? "earn" : "redeem",
    points: Math.abs(pts),
    orderId: "",
    orderAmount: 0,
    note: note || "Ajustement manuel",
    createdAt: new Date().toISOString(),
  };
  await saveLoyaltyTransaction(txn);
  return normalize(record);
};

const saveLoyaltyTransaction = async (txn) => {
  const normalized = {
    id: txn.id,
    customer_id: txn.customerId || txn.customer_id,
    type: txn.type,
    points: txn.points,
    order_id: txn.orderId || txn.order_id || "",
    order_amount: txn.orderAmount || txn.order_amount || 0,
    note: txn.note || "",
  };
  const cache = readCache(TRANSACTIONS_KEY);
  cache.unshift(normalized);
  writeCache(TRANSACTIONS_KEY, cache);
  try {
    if (supabase) {
      const { error } = await supabase.from("loyalty_transactions").insert(normalized);
      if (error) throw error;
    }
  } catch {}
  return normalizeTransaction(normalized);
};

export const getLoyaltyTransactions = async (customerId) => {
  const cache = readCache(TRANSACTIONS_KEY).filter((t) => t.customer_id === customerId || t.customerId === customerId);
  try {
    if (!supabase) return cache.map(normalizeTransaction);
    const { data, error } = await supabase.from("loyalty_transactions").select("*").eq("customer_id", customerId).order("created_at", { ascending: false });
    if (error) throw error;
    const rows = (data || []).map(normalizeTransaction);
    return rows;
  } catch { return cache.map(normalizeTransaction); }
};

export const deleteLoyaltyPoint = async (id) => {
  const cache = readCache(STORAGE_KEY).filter((r) => r.id !== id);
  writeCache(STORAGE_KEY, cache);
  try {
    if (supabase) {
      const { error } = await supabase.from("loyalty_points").delete().eq("id", id);
      if (error) throw error;
    }
  } catch {}
};
